/**
 * Local Storage Service
 *
 * Under the guest-token architecture (roadmap D22) localStorage holds
 * exactly one thing: the app token. Everything persistent - favorites,
 * ordering, custom links, dismissals, the streak - lives on the server,
 * reached with this token.
 *
 * The one deliberate exception lives elsewhere: `gameActivityStorage.js`
 * keeps the recent-game-click marker, which is genuinely transient,
 * device-scoped and only meaningful for ten minutes.
 *
 * Losing the token orphans the account (D11). That's accepted: the record
 * still exists and friends still see it, and it's a reason to sign in.
 */

const AUTH_TOKEN_KEY = 'authToken';

// Keys written by the pre-D22 client. Cleared on first load so a returning
// visitor doesn't carry a second, stale copy of state around forever.
const RETIRED_KEYS = [
  'favorites',
  'unlikedGames',
  'customLinks',
  'favoriteOrder',
  'refreshToken',
  'streak',
  'streakLastDate',
  'gamesPlayedToday',
  'gamesPlayedEver',
  'lastVisitedDate',
];

export const localStorageService = {
    /**
     * Get the app token
     * @returns {string|null} Token, or null if there isn't one yet
     */
    getAuthToken() {
        try {
            return localStorage.getItem(AUTH_TOKEN_KEY);
        } catch (error) {
            console.error('Error reading auth token from localStorage:', error);
            return null;
        }
    },

    /**
     * Store the app token
     * @param {string} token
     */
    setAuthToken(token) {
        try {
            localStorage.setItem(AUTH_TOKEN_KEY, token);
        } catch (error) {
            console.error('Error saving auth token to localStorage:', error);
            throw error;
        }
    },

    /**
     * Clear the app token
     */
    clearAuthToken() {
        try {
            localStorage.removeItem(AUTH_TOKEN_KEY);
        } catch (error) {
            console.error('Error clearing auth token from localStorage:', error);
        }
    },

    /**
     * Drop state written by the pre-D22 client. Purely hygiene - nothing
     * reads these keys any more, and the server is the source of truth.
     */
    clearRetiredState() {
        try {
            RETIRED_KEYS.forEach(key => localStorage.removeItem(key));
        } catch (error) {
            console.error('Error clearing retired localStorage state:', error);
        }
    },
};
